import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Upload, X, Loader2, Image as ImageIcon } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { api, formatApiErrorDetail } from '../App';
import { toast } from 'sonner';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { CATEGORIES, SUBCATEGORIES, CONDITIONS, GRADING_COMPANIES, isCardCategory } from '../lib/categories';

export default function CreateListingPage() {
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'coins',
    listing_type: 'buy_now',
    price: '',
    condition: 'good',
    location: '',
    trade_enabled: false,
    trade_preferences: '',
    auction_end_date: '',
    starting_bid: '',
    reserve_price: '',
    subcategory: '',
    player_character: '',
    set_series: '',
    year: '',
    is_graded: false,
    grading_company: '',
    grade: '',
  });
  
  const [images, setImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [listingId, setListingId] = useState(null);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (images.length + files.length > 10) {
      toast.error('Maximum 10 images allowed');
      return;
    }

    setUploading(true);
    for (const file of files) {
      if (!file.type.startsWith('image/')) {
        toast.error(`${file.name} is not an image`);
        continue;
      }
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`${file.name} is too large (max 10MB)`);
        continue;
      }

      // Create preview
      const reader = new FileReader();
      reader.onload = () => {
        setImages(prev => [...prev, { file, preview: reader.result, uploaded: false }]);
      };
      reader.readAsDataURL(file);
    }
    setUploading(false);
  };

  const removeImage = (index) => {
    setImages(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title || !formData.description || !formData.price) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSubmitting(true);
    try {
      // Create listing
      const listingData = {
        ...formData,
        price: parseFloat(formData.price),
        starting_bid: formData.listing_type === 'auction' ? parseFloat(formData.starting_bid || formData.price) : undefined,
        reserve_price: formData.listing_type === 'auction' && formData.reserve_price ? parseFloat(formData.reserve_price) : undefined,
        subcategory: formData.subcategory || null,
        player_character: formData.player_character || null,
        set_series: formData.set_series || null,
        year: formData.year || null,
        grading_company: formData.is_graded ? (formData.grading_company || null) : null,
        grade: formData.is_graded ? (formData.grade || null) : null,
      };

      const { data: listing } = await api.post('/listings', listingData);
      setListingId(listing.id);

      // Upload images
      for (const img of images) {
        if (img.file) {
          const formDataImg = new FormData();
          formDataImg.append('file', img.file);
          try {
            await api.post(`/listings/${listing.id}/images`, formDataImg, {
              headers: { 'Content-Type': 'multipart/form-data' }
            });
          } catch {
            // image upload error handled silently
          }
        }
      }

      toast.success('Listing created successfully!');
      navigate(`/listing/${listing.id}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-surface-base">
      <Header />
      
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="font-clash text-3xl sm:text-4xl font-medium text-white mb-2">
            Create Listing
          </h1>
          <p className="text-zinc-400">
            List your item for sale, auction, or trade
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Images */}
          <div className="p-6 bg-surface-card border border-border-subtle">
            <h2 className="font-clash text-lg font-medium text-white mb-4">Photos</h2>
            <p className="text-sm text-zinc-400 mb-4">Add up to 10 photos. First photo will be the cover.</p>
            
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-5 gap-4">
              {images.map((img, idx) => (
                <div key={img.preview} className="relative aspect-square bg-surface-elevated">
                  <img src={img.preview} alt="" className="w-full h-full object-cover" />
                  <button
                    type="button"
                    onClick={() => removeImage(idx)}
                    className="absolute top-2 right-2 w-6 h-6 bg-black/50 flex items-center justify-center hover:bg-black/70"
                  >
                    <X className="w-4 h-4 text-white" />
                  </button>
                  {idx === 0 && (
                    <span className="absolute bottom-2 left-2 px-2 py-0.5 bg-gold text-black text-xs font-semibold">
                      Cover
                    </span>
                  )}
                </div>
              ))}
              
              {images.length < 10 && (
                <label className="aspect-square bg-surface-elevated border-2 border-dashed border-border-subtle hover:border-gold cursor-pointer flex flex-col items-center justify-center transition-colors">
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleImageUpload}
                    className="hidden"
                    data-testid="image-upload-input"
                  />
                  {uploading ? (
                    <Loader2 className="w-8 h-8 text-gold animate-spin" />
                  ) : (
                    <>
                      <ImageIcon className="w-8 h-8 text-zinc-500 mb-2" />
                      <span className="text-xs text-zinc-500">Add Photo</span>
                    </>
                  )}
                </label>
              )}
            </div>
          </div>

          {/* Basic Info */}
          <div className="p-6 bg-surface-card border border-border-subtle space-y-6">
            <h2 className="font-clash text-lg font-medium text-white">Basic Information</h2>
            
            <div>
              <label className="block text-sm font-medium text-zinc-300 mb-2">Title *</label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => handleChange('title', e.target.value)}
                placeholder="e.g., 1921 Morgan Silver Dollar - MS65"
                className="input-dark w-full px-4 py-3"
                data-testid="listing-title-input"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-zinc-300 mb-2">Description *</label>
              <textarea
                value={formData.description}
                onChange={(e) => handleChange('description', e.target.value)}
                placeholder="Describe your item in detail..."
                className="input-dark w-full px-4 py-3 h-32 resize-none"
                data-testid="listing-description-input"
                required
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-zinc-300 mb-2">Category *</label>
                <Select value={formData.category} onValueChange={(v) => { handleChange('category', v); handleChange('subcategory', ''); }}>
                  <SelectTrigger className="w-full input-dark border-border-subtle" data-testid="listing-category-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-surface-card border-border-subtle">
                    {CATEGORIES.map(cat => (
                      <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {SUBCATEGORIES[formData.category] && (
                <div>
                  <label className="block text-sm font-medium text-zinc-300 mb-2">Subcategory</label>
                  <Select value={formData.subcategory || 'none'} onValueChange={(v) => handleChange('subcategory', v === 'none' ? '' : v)}>
                    <SelectTrigger className="w-full input-dark border-border-subtle" data-testid="listing-subcategory-select">
                      <SelectValue placeholder="Select subcategory" />
                    </SelectTrigger>
                    <SelectContent className="bg-surface-card border-border-subtle">
                      <SelectItem value="none">Select subcategory</SelectItem>
                      {SUBCATEGORIES[formData.category].map(sub => (
                        <SelectItem key={sub.value} value={sub.value}>{sub.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-zinc-300 mb-2">Condition *</label>
                <Select value={formData.condition} onValueChange={(v) => handleChange('condition', v)}>
                  <SelectTrigger className="w-full input-dark border-border-subtle" data-testid="listing-condition-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-surface-card border-border-subtle">
                    {CONDITIONS.map(cond => (
                      <SelectItem key={cond.value} value={cond.value}>{cond.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Card-specific fields */}
            {isCardCategory(formData.category) && (
              <div className="p-4 bg-surface-elevated border border-border-subtle space-y-4">
                <h3 className="text-sm font-medium text-gold uppercase tracking-wider">Card Details</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-zinc-300 mb-2">Player / Character</label>
                    <input
                      type="text"
                      value={formData.player_character}
                      onChange={(e) => handleChange('player_character', e.target.value)}
                      placeholder="e.g., Michael Jordan, Charizard"
                      className="input-dark w-full px-4 py-3"
                      data-testid="listing-player-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-zinc-300 mb-2">Set / Series</label>
                    <input
                      type="text"
                      value={formData.set_series}
                      onChange={(e) => handleChange('set_series', e.target.value)}
                      placeholder="e.g., Topps Chrome, Base Set"
                      className="input-dark w-full px-4 py-3"
                      data-testid="listing-set-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-zinc-300 mb-2">Year</label>
                    <input
                      type="text"
                      value={formData.year}
                      onChange={(e) => handleChange('year', e.target.value)}
                      placeholder="e.g., 2023"
                      className="input-dark w-full px-4 py-3"
                      data-testid="listing-year-input"
                    />
                  </div>
                </div>
                <div className="flex items-center gap-4 pt-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.is_graded}
                      onChange={(e) => handleChange('is_graded', e.target.checked)}
                      className="w-4 h-4 rounded border-border-subtle bg-surface-base text-gold"
                      data-testid="listing-graded-toggle"
                    />
                    <span className="text-sm text-zinc-300">Professionally Graded</span>
                  </label>
                </div>
                {formData.is_graded && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-zinc-300 mb-2">Grading Company</label>
                      <Select value={formData.grading_company || 'none'} onValueChange={(v) => handleChange('grading_company', v === 'none' ? '' : v)}>
                        <SelectTrigger className="w-full input-dark border-border-subtle" data-testid="listing-grader-select">
                          <SelectValue placeholder="Select grader" />
                        </SelectTrigger>
                        <SelectContent className="bg-surface-card border-border-subtle">
                          <SelectItem value="none">Select grader</SelectItem>
                          {GRADING_COMPANIES.map(g => (
                            <SelectItem key={g.value} value={g.value}>{g.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-zinc-300 mb-2">Grade</label>
                      <input
                        type="text"
                        value={formData.grade}
                        onChange={(e) => handleChange('grade', e.target.value)}
                        placeholder="e.g., PSA 10, BGS 9.5"
                        className="input-dark w-full px-4 py-3"
                        data-testid="listing-grade-input"
                      />
                    </div>
                  </div>
                )}
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-zinc-300 mb-2">Location</label>
              <input
                type="text"
                value={formData.location}
                onChange={(e) => handleChange('location', e.target.value)}
                placeholder="e.g., Los Angeles, CA"
                className="input-dark w-full px-4 py-3"
              />
            </div>
          </div>

          {/* Pricing */}
          <div className="p-6 bg-surface-card border border-border-subtle space-y-6">
            <h2 className="font-clash text-lg font-medium text-white">Pricing & Type</h2>
            
            <div>
              <label className="block text-sm font-medium text-zinc-300 mb-2">Listing Type</label>
              <div className="flex gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="listing_type"
                    value="buy_now"
                    checked={formData.listing_type === 'buy_now'}
                    onChange={(e) => handleChange('listing_type', e.target.value)}
                    className="w-4 h-4 text-gold"
                  />
                  <span className="text-zinc-300">Buy Now</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="listing_type"
                    value="auction"
                    checked={formData.listing_type === 'auction'}
                    onChange={(e) => handleChange('listing_type', e.target.value)}
                    className="w-4 h-4 text-gold"
                  />
                  <span className="text-zinc-300">Auction</span>
                </label>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-zinc-300 mb-2">
                  {formData.listing_type === 'auction' ? 'Starting Bid *' : 'Price *'}
                </label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-500">$</span>
                  <input
                    type="number"
                    value={formData.price}
                    onChange={(e) => handleChange('price', e.target.value)}
                    placeholder="0.00"
                    className="input-dark w-full pl-8 pr-4 py-3"
                    data-testid="listing-price-input"
                    required
                  />
                </div>
              </div>

              {formData.listing_type === 'auction' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-zinc-300 mb-2">Reserve Price (optional)</label>
                    <div className="relative">
                      <span className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-500">$</span>
                      <input
                        type="number"
                        value={formData.reserve_price}
                        onChange={(e) => handleChange('reserve_price', e.target.value)}
                        placeholder="0.00"
                        className="input-dark w-full pl-8 pr-4 py-3"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-zinc-300 mb-2">Auction End Date *</label>
                    <input
                      type="datetime-local"
                      value={formData.auction_end_date}
                      onChange={(e) => handleChange('auction_end_date', e.target.value)}
                      className="input-dark w-full px-4 py-3"
                      required={formData.listing_type === 'auction'}
                    />
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Trade Options */}
          <div className="p-6 bg-surface-card border border-border-subtle space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="font-clash text-lg font-medium text-white">Trade Options</h2>
                <p className="text-sm text-zinc-400">Allow buyers to propose trades</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.trade_enabled}
                  onChange={(e) => handleChange('trade_enabled', e.target.checked)}
                  className="sr-only peer"
                  data-testid="trade-enabled-toggle"
                />
                <div className="w-11 h-6 bg-border-subtle peer-focus:ring-2 peer-focus:ring-gold peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:h-5 after:w-5 after:transition-all peer-checked:bg-gold"></div>
              </label>
            </div>

            {formData.trade_enabled && (
              <div>
                <label className="block text-sm font-medium text-zinc-300 mb-2">Trade Preferences</label>
                <textarea
                  value={formData.trade_preferences}
                  onChange={(e) => handleChange('trade_preferences', e.target.value)}
                  placeholder="What are you interested in trading for?"
                  className="input-dark w-full px-4 py-3 h-24 resize-none"
                />
              </div>
            )}
          </div>

          {/* Submit */}
          <div className="flex gap-4">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="btn-secondary px-8 py-4"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="btn-gold flex-1 py-4 flex items-center justify-center gap-2 disabled:opacity-50"
              data-testid="create-listing-submit"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" /> Creating...
                </>
              ) : (
                'Create Listing'
              )}
            </button>
          </div>
        </form>
      </main>

      <Footer />
    </div>
  );
}
